import { useEffect, useMemo, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowsClockwise, CheckCircle, Copy, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * "Create deep link" — the same form as RGA's portal, run through RGA's
 * placements API with the tab's account token. The new placement becomes a
 * brand on that account (RGA's merchant rates as its payout), ready to
 * assign. Merchants come from RGA's own list (GET /v1/merchants, kept warm
 * by the backend) with their sites and rates; if that is unavailable, the
 * merchants this account already has placements for.
 */

const rateText = (r) => (r && r.max != null
  ? (Number(r.min) === Number(r.max) ? `${r.max}%` : `${r.min}–${r.max}%`) : "");
const EMPTY = { merchant: "", url: "", label: "" };

function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, "").toLowerCase(); } catch { return ""; }
}

export default function CreateDeepLinkDialog({ open, onOpenChange, account, accountLabel, onCreated }) {
  const [merchants, setMerchants] = useState([]);
  const [meta, setMeta] = useState({ source: "", error: "" });
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState(null);   // RGA's merchants when a site is ambiguous
  const [result, setResult] = useState(null);

  const loadMerchants = (refresh = false) => {
    setLoadingMerchants(true);
    return API.get("/admin/rga/merchants", { params: { account, refresh } })
      .then(({ data }) => {
        setMerchants(data.merchants || []);
        setMeta({ source: data.source, error: data.error || "" });
      })
      .catch(() => { setMerchants([]); setMeta({ source: "", error: "Could not load merchants" }); })
      .finally(() => setLoadingMerchants(false));
  };

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY); setChoices(null); setResult(null);
    loadMerchants(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, account]);

  const chosen = merchants.find((m) => m.name === form.merchant);
  const host = hostOf(form.url);
  // A soft hint only: RGA decides which sites a merchant accepts.
  const offSite = Boolean(chosen?.domains?.length && host
    && !chosen.domains.some((d) => host === d || host.endsWith(`.${d}`)));
  const urlOk = /^https:\/\/\S+/i.test(form.url.trim()) && form.url.trim().length <= 2000;
  const ready = form.merchant && urlOk && form.label.trim() && form.label.trim().length <= 100;

  const submit = async (merchantId = "") => {
    setBusy(true);
    try {
      const { data } = await API.post("/admin/rga/deeplinks", {
        merchant: form.merchant, url: form.url.trim(), label: form.label.trim(),
        merchant_id: merchantId || chosen?.uuid || "",
      }, { params: { account } });
      if (data.needs_merchant) {
        setChoices(data.merchants || []);
        return;
      }
      setChoices(null);
      setResult(data);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the deep link");
    } finally {
      setBusy(false);
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copied"));

  const merchantOptions = useMemo(() => merchants.map((m) => (
    <SelectItem key={m.name} value={m.name}>
      <span className="font-medium">{m.name}</span>
      {m.domains?.length ? <span className="ml-2 text-slate-400">{m.domains.slice(0, 2).join(", ")}</span> : null}
      {rateText(m.rates) ? <span className="ml-2 text-emerald-700">{rateText(m.rates)}</span> : null}
    </SelectItem>
  )), [merchants]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="rga-create-deeplink-dialog">
        <DialogHeader>
          <DialogTitle>Create deep link</DialogTitle>
          <DialogDescription>
            Send visitors straight to a page on a merchant's site. It's created on {accountLabel}'s
            RGA account, is live as soon as it's created, and is added here as a brand.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="rga-deeplink-result">
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              <CheckCircle size={18} weight="fill" className="mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">{result.brand?.name || "Deep link"} is live</div>
                <div className="text-xs text-emerald-700">
                  {result.created_on_rga ? "Created on RGA" : "RGA already had this exact URL — reused it"}
                  {" · "}{result.brand_created ? "new brand added" : "existing brand updated"}
                  {result.brand?.payout_display ? ` · pays ${result.brand.payout_display}` : ""}
                </div>
              </div>
            </div>
            {result.warning && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0" /> {result.warning}
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">Tracking URL</Label>
              <div className="mt-1 flex gap-2">
                <code className="flex-1 truncate rounded border border-slate-200 bg-slate-50 px-2 py-2 text-xs">{result.tracking_url}</code>
                <Button size="sm" variant="outline" onClick={() => copy(result.tracking_url)}>
                  <Copy size={14} className="mr-1" /> Copy
                </Button>
              </div>
              <p className="mt-1 text-[11px] text-slate-500">Assign the brand to publishers in Step 2 — they get our tracked link, not this one.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setResult(null); setForm({ ...EMPTY, merchant: form.merchant }); }}>
                Create another
              </Button>
              <Button className="bg-red-700 hover:bg-red-800 text-white" onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); if (ready && !busy) submit(); }}>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Merchant</Label>
                <button type="button" className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800"
                  onClick={() => loadMerchants(true)} disabled={loadingMerchants} title="Reload RGA's merchant list">
                  <ArrowsClockwise size={12} className={loadingMerchants ? "animate-spin" : ""} />
                  {loadingMerchants ? "Loading from RGA…" : "Refresh"}
                </button>
              </div>
              <Select value={form.merchant} onValueChange={(v) => setForm({ ...form, merchant: v })}>
                <SelectTrigger data-testid="rga-deeplink-merchant"><SelectValue placeholder="Choose a merchant…" /></SelectTrigger>
                <SelectContent>{merchantOptions}</SelectContent>
              </Select>
              {chosen && (chosen.domains?.length || rateText(chosen.rates)) ? (
                <p className="text-[11px] text-slate-500">
                  {chosen.domains?.length ? <>Sites: {chosen.domains.join(", ")}</> : null}
                  {chosen.domains?.length && rateText(chosen.rates) ? " · " : null}
                  {rateText(chosen.rates) ? <>Pays {rateText(chosen.rates)} of each sale</> : null}
                </p>
              ) : null}
              {meta.source === "brands" && (
                <p className="text-[11px] text-amber-700">
                  {meta.error || "RGA's merchant list isn't loaded yet"} — showing merchants from this account's brands.
                </p>
              )}
              {!merchants.length && !loadingMerchants && (
                <p className="text-[11px] text-slate-500">No merchants available for this account.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Destination URL</Label>
              <p className="text-xs text-slate-500">The merchant page to send visitors to. It can't be changed after the link is created.</p>
              <Input value={form.url} placeholder={chosen?.domains?.[0] ? `https://www.${chosen.domains[0]}/page` : "https://www.example.com/page"}
                onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="rga-deeplink-url" />
              {form.url && !urlOk && <p className="text-[11px] text-red-700">Start with https:// (up to 2,000 characters).</p>}
              {urlOk && offSite && (
                <p className="text-[11px] text-amber-700">
                  {host} isn't one of {form.merchant}'s known sites ({chosen.domains.join(", ")}). RGA decides — it may reject it.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Label</Label>
              <p className="text-xs text-slate-500">Helps you tell your links apart. Shown as {form.merchant || "Merchant"} · {form.label || "Label"}.</p>
              <Input value={form.label} maxLength={100} placeholder="e.g. Swimwear"
                onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="rga-deeplink-label" />
            </div>

            {choices && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="rga-deeplink-choices">
                <p className="mb-2 text-xs text-amber-800">More than one RGA merchant accepts this site. Pick the right one:</p>
                <div className="flex flex-wrap gap-2">
                  {choices.map((m) => (
                    <Button key={m.uuid} type="button" size="sm" variant="outline" disabled={busy} onClick={() => submit(m.uuid)}>
                      {m.label || m.uuid}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" className="bg-red-700 hover:bg-red-800 text-white" disabled={!ready || busy}
                data-testid="rga-deeplink-create">
                {busy ? "Creating…" : "Create link"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
